<template>
  <div class="card">
    <!-- Path with parent and element -->
    <header class="card-header">
      <p class="card-header-title is-block has-text-grey">
        <template v-if="document.parent_id !== null && document.parent_id !== document.element_id">
          <span
            :title="document.parent_type"
            class="mr-1"
          >
            {{ truncateShort(document.parent_type) }}
          </span>
          <router-link
            :to="{ name: 'element-details', params: { id: document.parent_id } }"
            :title="document.parent_name"
          >
            {{ truncateLong(document.parent_name) }}
          </router-link>
          /
        </template>
        <span
          :title="document.element_type"
          class="mr-1"
        >
          {{ truncateShort(document.element_type) }}
        </span>
        <router-link
          :to="{ name: 'element-details', params: { id: document.element_id } }"
          :title="document.element_text"
        >
          {{ truncateLong(document.element_text) }}
        </router-link>
      </p>
    </header>
    <!-- Image -->
    <div class="card-image">
      <figure class="image">
        <img
          src="@/assets/missingPreview.jpg"
          v-if="!document.element_image || imgFailed"
        />
        <img
          :src="document.element_image"
          v-on:error="imgFailed = true"
          v-else
        />
      </figure>
    </div>
    <!-- Additional information -->
    <div class="card-content">
      <!-- Transcription -->
      <template v-if="document.transcription_id">
        <div class="has-text-centered">
          <span class="text">{{ truncated(document.transcription_text) }}</span>
          <ConfidenceTag
            v-if="
              document.transcription_confidence !== null &&
              Number.isFinite(document.transcription_confidence)
            "
            :value="document.transcription_confidence"
          />
        </div>
      </template>
      <!-- Classification -->
      <template v-if="document.classification_id">
        <div class="tags has-addons mb-0 is-justify-content-center">
          <span class="tag is-warning">{{ document.classification_name }}</span>
          <ConfidenceTag
            v-if="
              document.classification_confidence !== null &&
              Number.isFinite(document.classification_confidence)
            "
            :value="document.classification_confidence"
          />
        </div>
      </template>
      <!-- Metadata -->
      <template v-if="document.metadata_id">
        <div class="has-text-centered">
          <span :title="document.metadata_type">
            <i :class="iconClass(document.metadata_type)"></i>
          </span>
          <span class="metadata-content">
            {{ document.metadata_name }} : <strong>{{ document.metadata_text }}</strong>
          </span>
        </div>
      </template>
      <!-- Transcription entity -->
      <template v-if="document.transcription_entity_id">
        <div class="has-text-centered">
          <span
            >{{ document.transcription_entity_text }} ({{
              document.transcription_entity_type
            }})</span
          >
        </div>
      </template>
    </div>
    <!-- View element button -->
    <div class="card-footer">
      <router-link
        class="button"
        :to="{
          name: 'element-details',
          params: { id: document.parent_id },
          query: { highlight: document.element_id },
        }"
      >
        View element
      </router-link>
    </div>
  </div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from 'vue'

import ConfidenceTag from '@/components/ConfidenceTag.vue'
import { METADATA_TYPES } from '@/config'
import { truncateMixin } from '@/mixins'
import type { MetaDataType } from '@/types/metadata'
import type { SearchResult } from '@/types/search'

export default defineComponent({
  components: {
    ConfidenceTag,
  },
  mixins: [truncateMixin],
  props: {
    document: {
      type: Object as PropType<SearchResult>,
      required: true,
    },
  },
  data: () => ({
    METADATA_TYPES,
    imgFailed: false,
  }),
  methods: {
    iconClass(type: MetaDataType) {
      return METADATA_TYPES[type].icon || 'icon-feather'
    },
    truncated(text: string) {
      if (text.length < 100) return text
      return text.substring(0, 100) + '[...]'
    },
  },
})
</script>

<style scoped>
span.text {
  margin-right: 0.5em;
}

.card-content {
  position: relative;
}

.card-header-title {
  font-weight: normal;
}

.button {
  width: 100%;
}
</style>
